import store from 'js/lib/coursera.store';

import { plainTextConverter } from 'bundles/cml';
import type { Response as ChangedResponseType } from 'bundles/compound-assessments/components/local-state/changed-response/types';
import { GraderTypes, typeNames } from 'bundles/compound-assessments/constants';
import * as gradingCmlUtils from 'bundles/grading-common/utils/cmlUtils';
import type { AiReviewPartSuggestion } from 'bundles/grading/api/types';

const DEFAULT_AI_SUGGESTION_INTERACTION_LOG_KEY = 'quick-grader-interaction-log';

// 4 months in milliseconds, we can assume most privates session terms end in 3-4 months so we can remove
// delete those entries just to avoid polluting the local storage over time
const DEFAULT_STALE_LOG_ITEM_MS = 10519200000;

export type AiSuggestionRating = 'LIKE' | 'DISLIKE' | null;
export type AiSuggestionInteractionItem = {
  timestamp: number;
  applied: boolean;
  edited: boolean;
  rating: AiSuggestionRating;
};

// here the key is suggestionId
export type AiSuggestionInteractionLog = Record<string, AiSuggestionInteractionItem>;

/**
 * Removes stale log items from the local storage
 * We may move this state management to BE eventually so we can
 * automate local storage cleanup for client over time
 */
export const removeStaleInteractions = () => {
  const interactionLog: AiSuggestionInteractionLog = store.get(DEFAULT_AI_SUGGESTION_INTERACTION_LOG_KEY);
  if (interactionLog) {
    const currentTimestamp = Date.now();
    for (const key in interactionLog) {
      // ensure this key exists in the log, and check if it's stale; if so delete the log item
      if (
        interactionLog?.[key]?.timestamp &&
        currentTimestamp - interactionLog[key].timestamp > DEFAULT_STALE_LOG_ITEM_MS
      ) {
        delete interactionLog[key];
      }
    }

    // reset updated log to local storage
    store.set(DEFAULT_AI_SUGGESTION_INTERACTION_LOG_KEY, interactionLog);
  }
};

/**
 * Gets the interaction log from local storage, null if non existent
 *
 * @returns AiSuggestionInteractionLog | null
 */
export const getAiSuggestionInteractionLogFromLocalStorage = () => {
  const interactionLog: AiSuggestionInteractionLog = store.get(DEFAULT_AI_SUGGESTION_INTERACTION_LOG_KEY);
  return interactionLog || null;
};

/**
 * Adds interactions to the log
 * @param suggestionId
 * @param applied
 * @param rating
 */
export const addAiSuggestionInteractionToLog = (
  suggestionId: string,
  applied: boolean,
  edited: boolean,
  rating: AiSuggestionRating
) => {
  const logItemToSave: AiSuggestionInteractionLog = {
    [suggestionId]: {
      timestamp: Math.ceil(Date.now() / 10000) * 10000, // round to the nearest 10-seconds to avoid unnecessary extra reassignments to local storage
      applied,
      edited,
      rating,
    },
  };

  // grab log from local storage; if it exists, update the log and set back into local storage,
  // otherwise set new logItem to local storage
  const interactionLog: AiSuggestionInteractionLog = store.get(DEFAULT_AI_SUGGESTION_INTERACTION_LOG_KEY);
  if (interactionLog) {
    const result = Object.assign(interactionLog, logItemToSave);
    store.set(DEFAULT_AI_SUGGESTION_INTERACTION_LOG_KEY, result);
  } else {
    store.set(DEFAULT_AI_SUGGESTION_INTERACTION_LOG_KEY, logItemToSave);
  }
};

/**
 * Gets the appropriate changeResponse input object given
 * the ai suggestion for the respective form part
 *
 * @param aiReviewPartSuggestion
 * @returns ChangedResponseType | null
 */
export const getChangeResponseInputFromAiSuggestion = (
  aiReviewPartSuggestion: AiReviewPartSuggestion
): ChangedResponseType | null => {
  let optionsRichTextInput;
  switch (aiReviewPartSuggestion.details.__typename) {
    case 'AiSuggestions_GradedTextBoxDetails':
      return {
        typeName: typeNames.REVIEW_RESPONSE,
        definition: {
          // set to Human, as the suggestion is applied by a Human Grader
          graderType: GraderTypes.Human,
          reviewPart: {
            typeName: 'multiLineInput',
            definition: {
              input: aiReviewPartSuggestion.details.input,
              score: aiReviewPartSuggestion.details.score,
            },
          },
        },
      };
    case 'AiSuggestions_OptionsDetails':
      if (aiReviewPartSuggestion.details.feedback) {
        optionsRichTextInput = plainTextConverter.toCML(
          aiReviewPartSuggestion.details.feedback,
          gradingCmlUtils.GRADING_DTD_ID
        );
      }
      return {
        typeName: typeNames.REVIEW_RESPONSE,
        definition: {
          // set to Human, as the suggestion is applied by a Human Grader
          graderType: GraderTypes.Human,
          reviewPart: {
            definition: {
              choice: aiReviewPartSuggestion.details.optionChoice,
              richTextInput: optionsRichTextInput,
            },
            typeName: typeNames.OPTIONS,
          },
        },
      };
    case 'AiSuggestions_UngradedTextBoxDetails':
      return {
        typeName: typeNames.REVIEW_RESPONSE,
        definition: {
          // set to Human, as the suggestion is applied by a Human Grader
          graderType: GraderTypes.Human,
          reviewPart: {
            typeName: 'multiLineInput',
            definition: {
              input: aiReviewPartSuggestion.details.input,
            },
          },
        },
      };
    default:
      return null;
  }
};
